import type { EditableFields, InquiryRecord } from './model';

/** Inline editor values: only the fields the PATCH endpoint accepts. */
export interface EditorValues {
  guest_name: string;
  phone: string;
  email: string;
  occasion: string;
  /** yyyy-mm-dd, or the free text Arthur heard. */
  preferred_date: string;
  preferred_time: string;
  guest_count: string;
  special_requests: string;
}

export type EditorErrors = Partial<Record<keyof EditorValues, string>>;

export function valuesFrom(e: InquiryRecord): EditorValues {
  return {
    guest_name: e.guestName ?? '',
    phone: e.phone ?? '',
    email: e.email ?? '',
    occasion: e.occasion ?? '',
    preferred_date: e.eventDate ?? e.eventDateRaw ?? '',
    preferred_time: e.eventTime ?? '',
    guest_count:
      e.guestCount != null && String(e.guestCount) === e.guestCountRaw
        ? String(e.guestCount)
        : (e.guestCountRaw ?? ''),
    special_requests: e.specialRequests ?? '',
  };
}

export function validate(v: EditorValues): EditorErrors {
  const errors: EditorErrors = {};
  if (!v.guest_name.trim() && !v.phone.trim()) {
    errors.guest_name =
      'Add a name or a phone number so the team can reach the guest.';
  }
  if (v.phone.trim() && v.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Enter a 10-digit phone number.';
  }
  if (v.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) {
    errors.email = 'That email address doesn’t look right.';
  }
  return errors;
}

/**
 * Only the fields that changed, trimmed. Compared against the values the
 * editor opened with, so untouched fields Arthur filled from parsed fields
 * aren't written back.
 */
export function toPatch(
  v: EditorValues,
  original: EditorValues,
): EditableFields {
  const patch: EditableFields = {};
  (Object.keys(v) as Array<keyof EditorValues>).forEach((k) => {
    const next = v[k].trim();
    if (next !== original[k].trim()) patch[k] = next;
  });
  return patch;
}

/** Notes persist by appending to `follow_up_notes`: "[Oct 1, 9:05 AM · Dana] text". */
export function appendNote(
  existing: string | null,
  text: string,
  author: string,
  now = new Date(),
): string {
  const stamp = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(now);
  const entry = `[${stamp} · ${author}] ${text.trim()}`;
  return existing?.trim() ? `${existing.trim()}\n\n${entry}` : entry;
}
